import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { useSearchParams } from 'react-router'

import { CommercialAgreementDetailsProps } from '@/components/misc/CommercialAgreementsDetails/CommercialAgreementsDetails'
import DirectionAgreementDetails from '@/components/misc/CommercialAgreementsDetails/components/DirectionAgreementDetails'
import {
  AGREEMENT_TABS,
  COMMERCIAL_AGREEMENT_TAB_KEY,
  COMMERCIAL_AGREEMENT_TAB_PARAMS,
} from '@/components/misc/CommercialAgreementsDetails/constants'
import { isValidCommercialAgreementTabKey } from '@/components/misc/CommercialAgreementsDetails/helpers'
import TabTitle from '@/components/misc/CompanyProfile/components/TabTitle'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { PANEL_AGREEMENT_MESSAGE, PENDING_PANEL_ACTION } from '@/lib/constants'
import { faExclamationTriangle } from '@/lib/fontAwesomePro/regular'
import { isDevelopment } from '@/lib/helpers/envHelpers'
import commercialAgreementService from '@/lib/services/commercialAgreementService'
import { CommercialAgreementReferralDirection } from '@/types/misc'
import { PanelItem } from '@/types/responses/panels'

type Props = Pick<
  CommercialAgreementDetailsProps,
  'onConfirmAgreement' | 'variant'
> & {
  panel: PanelItem
  direction: CommercialAgreementReferralDirection
}

const DirectionAgreements: React.FC<Props> = ({
  panel,
  direction,
  onConfirmAgreement,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const otherCompany =
    currentCompany.id === panel.fromCompany.id
      ? panel.toCompany
      : panel.fromCompany
  const currentCompanyName = currentCompany.presentationName
  const otherCompanyName = otherCompany.presentationName
  const [searchParams, setSearchParams] = useSearchParams()

  const outgoingAgreementTabFromSearchParam = searchParams.get(
    COMMERCIAL_AGREEMENT_TAB_PARAMS.outgoingAgreementTab,
  )
  const incomingAgreementTabFromSearchParam = searchParams.get(
    COMMERCIAL_AGREEMENT_TAB_PARAMS.incomingAgreementTab,
  )

  const isOutgoingDirection =
    commercialAgreementService.isOutgoingAgreementDirection(direction)

  const tabKeyName = isOutgoingDirection
    ? COMMERCIAL_AGREEMENT_TAB_PARAMS.outgoingAgreementTab
    : COMMERCIAL_AGREEMENT_TAB_PARAMS.incomingAgreementTab

  const pendingAgreement = isOutgoingDirection
    ? panel.pendingOutgoingAgreement
    : panel.pendingIncomingAgreement

  function getOutgoingAgreementTab() {
    let outgoingAgreementTab = isValidCommercialAgreementTabKey(
      outgoingAgreementTabFromSearchParam,
    )
      ? outgoingAgreementTabFromSearchParam
      : COMMERCIAL_AGREEMENT_TAB_KEY.current

    if (
      outgoingAgreementTab === COMMERCIAL_AGREEMENT_TAB_KEY.pending &&
      !panel.pendingOutgoingAgreement
    ) {
      outgoingAgreementTab = COMMERCIAL_AGREEMENT_TAB_KEY.current
    }

    return outgoingAgreementTab
  }

  const outgoingAgreementTab = getOutgoingAgreementTab()

  function getIncomingAgreementTab() {
    let incomingAgreementTab = isValidCommercialAgreementTabKey(
      incomingAgreementTabFromSearchParam,
    )
      ? incomingAgreementTabFromSearchParam
      : COMMERCIAL_AGREEMENT_TAB_KEY.current

    if (
      incomingAgreementTab === COMMERCIAL_AGREEMENT_TAB_KEY.pending &&
      !panel.pendingIncomingAgreement
    ) {
      incomingAgreementTab = COMMERCIAL_AGREEMENT_TAB_KEY.current
    }

    return incomingAgreementTab
  }

  const incomingAgreementTab = getIncomingAgreementTab()

  const activeTabKey = isOutgoingDirection
    ? outgoingAgreementTab
    : incomingAgreementTab

  const isShowingCurrentOutgoingAgreement =
    outgoingAgreementTab === COMMERCIAL_AGREEMENT_TAB_KEY.current

  const outgoingAgreement = isShowingCurrentOutgoingAgreement
    ? panel.currentOutgoingAgreement
    : panel.pendingOutgoingAgreement

  const isShowingCurrentIncomingAgreement =
    incomingAgreementTab === COMMERCIAL_AGREEMENT_TAB_KEY.current

  const incomingAgreement = isShowingCurrentIncomingAgreement
    ? panel.currentIncomingAgreement
    : panel.pendingIncomingAgreement

  const agreement = isOutgoingDirection ? outgoingAgreement : incomingAgreement

  const otherAgreement = isOutgoingDirection
    ? incomingAgreement
    : outgoingAgreement

  return (
    <section>
      <TabTitle>
        {isOutgoingDirection ? (
          <>
            Commercial terms for referrals from {currentCompanyName} to{' '}
            {otherCompanyName}
          </>
        ) : (
          <>
            Commercial terms for referrals from {otherCompanyName} to{' '}
            {currentCompanyName}
          </>
        )}
      </TabTitle>

      {pendingAgreement && (
        <div>
          {AGREEMENT_TABS.map((tab) => {
            const isActive = tab.key === activeTabKey
            const isCurrentTab =
              tab.key === COMMERCIAL_AGREEMENT_TAB_KEY.current
            const isPendingTab =
              tab.key === COMMERCIAL_AGREEMENT_TAB_KEY.pending

            function getButtonVariant(): React.ComponentProps<
              typeof Button
            >['variant'] {
              if (isActive) {
                return 'black'
              }

              return isPendingTab ? 'outline-warning' : 'outline-secondary'
            }

            function getAgreementId() {
              if (isOutgoingDirection) {
                return isCurrentTab
                  ? panel.currentOutgoingAgreement?.id
                  : panel.pendingOutgoingAgreement?.id
              }

              return isCurrentTab
                ? panel.currentIncomingAgreement?.id
                : panel.pendingIncomingAgreement?.id
            }

            const agreementId = getAgreementId()

            const tooltipTitle =
              isDevelopment() && agreementId
                ? `Agreement ID: ${agreementId}`
                : undefined

            return (
              <Button
                key={tab.key}
                title={tooltipTitle}
                variant={getButtonVariant()}
                className="inline-flex w-full items-center gap-2 rounded-none
                  sm:w-auto"
                onClick={() => {
                  searchParams.set(tabKeyName, tab.key)
                  setSearchParams(searchParams)
                }}
              >
                {tab.label}

                {isPendingTab && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </TooltipTrigger>

                    <TooltipContent>
                      {panel.pendingActions.includes(
                        PENDING_PANEL_ACTION.reviewPendingAgreement,
                      )
                        ? PANEL_AGREEMENT_MESSAGE.reviewAgreement
                        : PANEL_AGREEMENT_MESSAGE.chaseAgreementReview}
                    </TooltipContent>
                  </Tooltip>
                )}
              </Button>
            )
          })}
        </div>
      )}

      <div className="mt-4">
        <DirectionAgreementDetails
          panel={panel}
          agreement={agreement}
          otherAgreementNeedsReview={!!otherAgreement?.needsReview}
          referralDirection={direction}
          onConfirmAgreement={async () => {
            if (onConfirmAgreement) {
              await onConfirmAgreement()
            }
          }}
        />
      </div>
    </section>
  )
}

export default DirectionAgreements
